import ErrorBoundary from '@components/ErrorBoundary';
import { jwtDecode } from 'jwt-decode';
import { createContext, useEffect, useState } from 'react';
import { subscribeAccessToken, getAccessToken } from '../utils/authTokenStore';
import { refreshAccessToken, startActivityHeartbeat } from '../utils/authSession';

const SessionContext = createContext();

const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const syncFromToken = (nextToken) => {
    if (!nextToken) {
      setToken(null);
      setIsAuthenticated(false);
      return;
    }
    try {
      const decodedToken = jwtDecode(nextToken);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        setToken(null);
        setIsAuthenticated(false);
        return;
      }
      setToken(nextToken);
      setIsAuthenticated(true);
    } catch {
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken(null);
  };

  useEffect(() => {
    const existing = getAccessToken();
    if (existing) {
      syncFromToken(existing);
      setLoading(false);
      return;
    }

    refreshAccessToken()
      .then((refreshed) => {
        syncFromToken(refreshed);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    return subscribeAccessToken((nextToken) => {
      syncFromToken(nextToken);
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    return startActivityHeartbeat(getAccessToken);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const interval = setInterval(async () => {
      const current = getAccessToken();
      if (!current) {
        const refreshed = await refreshAccessToken();
        syncFromToken(refreshed);
        return;
      }
      try {
        const decodedToken = jwtDecode(current);
        const secondsLeft = decodedToken.exp - Date.now() / 1000;
        if (secondsLeft < 120) {
          const refreshed = await refreshAccessToken();
          syncFromToken(refreshed || current);
        }
      } catch {
        handleLogout();
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <SessionContext.Provider
      value={{ isAuthenticated, loading, setIsAuthenticated, token, handleLogout }}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </SessionContext.Provider>
  );
};

export { SessionContext, SessionProvider };
